import { useState, useEffect } from 'react'
import { ExternalLink, Download, Copy, Trash2, Webhook, FlaskConical, Pencil, KeyRound } from 'lucide-react'
import type { AppEntry } from '../store/appsSlice'
import { useAppSelector } from '../store/hooks'
import CopyAppModal from './CopyAppModal'
import DeleteAppModal from './DeleteAppModal'
import EditAppModal from './EditAppModal'
import WebhooksModal from './WebhooksModal'
import AppSecretsModal from './AppSecretsModal'
import type { AppSecretEntry } from './AppSecretsModal'
import './AppActions.css'

interface AppActionsProps {
  app: AppEntry
}

function AppActions({ app }: AppActionsProps) {
  const [showCopyModal, setShowCopyModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showWebhooksModal, setShowWebhooksModal] = useState(false)
  const [showSecretsModal, setShowSecretsModal] = useState(false)
  const [secretEntries, setSecretEntries] = useState<AppSecretEntry[]>([])
  const user = useAppSelector((state) => state.auth.user)

  useEffect(() => {
    let cancelled = false
    fetch(`/api/app-allowlist/${app.id}`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data: AppSecretEntry[]) => {
        if (!cancelled) setSecretEntries(Array.isArray(data) ? data : [])
      })
      .catch(() => {
        if (!cancelled) setSecretEntries([])
      })
    return () => {
      cancelled = true
    }
  }, [app.id])

  return (
    <>
      <div className="app-actions" role="group" aria-label="App actions" data-testid="app-actions">
        {app.deployment_url ? (
          <a
            className="app-actions__btn app-actions__btn--primary"
            href={app.deployment_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="app-actions-open"
          >
            <ExternalLink size={18} aria-hidden="true" />
            <span className="app-actions__btn-label" data-testid="app-actions-open-label">Open Live App</span>
          </a>
        ) : (
          <button
            className="app-actions__btn app-actions__btn--primary app-actions__btn--disabled"
            disabled
            data-testid="app-actions-open"
          >
            <ExternalLink size={18} aria-hidden="true" />
            <span className="app-actions__btn-label" data-testid="app-actions-open-label">Open Live App</span>
          </button>
        )}
        {app.source_url ? (
          <a
            className="app-actions__btn app-actions__btn--secondary"
            href={app.source_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="app-actions-source"
          >
            <Download size={18} aria-hidden="true" />
            <span className="app-actions__btn-label" data-testid="app-actions-source-label">Download Source Code</span>
          </a>
        ) : (
          <button
            className="app-actions__btn app-actions__btn--secondary app-actions__btn--disabled"
            disabled
            data-testid="app-actions-source"
          >
            <Download size={18} aria-hidden="true" />
            <span className="app-actions__btn-label" data-testid="app-actions-source-label">Download Source Code</span>
          </button>
        )}
        <button
          className="app-actions__btn app-actions__btn--copy"
          onClick={() => setShowCopyModal(true)}
          data-testid="app-actions-copy"
        >
          <Copy size={18} aria-hidden="true" />
          <span className="app-actions__btn-label" data-testid="app-actions-copy-label">Copy App</span>
        </button>
        <button
          className="app-actions__btn app-actions__btn--webhooks"
          onClick={() => setShowWebhooksModal(true)}
          data-testid="app-actions-webhooks"
        >
          <Webhook size={18} aria-hidden="true" />
          <span className="app-actions__btn-label" data-testid="app-actions-webhooks-label">Webhooks</span>
        </button>
        {secretEntries.length > 0 && (
          <button
            className="app-actions__btn app-actions__btn--secrets"
            onClick={() => setShowSecretsModal(true)}
            data-testid="app-actions-secrets"
          >
            <KeyRound size={18} aria-hidden="true" />
            <span className="app-actions__btn-label" data-testid="app-actions-secrets-label">Secrets</span>
          </button>
        )}
        {app.qa_project_url && (
          <a
            className="app-actions__btn app-actions__btn--qa"
            href={app.qa_project_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="app-actions-qa"
          >
            <FlaskConical size={18} aria-hidden="true" />
            <span className="app-actions__btn-label" data-testid="app-actions-qa-label">Loop QA</span>
          </a>
        )}
        {user && (
          <button
            className="app-actions__btn app-actions__btn--edit"
            onClick={() => setShowEditModal(true)}
            data-testid="app-actions-edit"
          >
            <Pencil size={18} aria-hidden="true" />
            <span className="app-actions__btn-label" data-testid="app-actions-edit-label">Edit</span>
          </button>
        )}
        {user && (
          <button
            className="app-actions__btn app-actions__btn--delete"
            onClick={() => setShowDeleteModal(true)}
            data-testid="app-actions-delete"
          >
            <Trash2 size={18} aria-hidden="true" />
            <span className="app-actions__btn-label" data-testid="app-actions-delete-label">Delete</span>
          </button>
        )}
      </div>
      {showCopyModal && (
        <CopyAppModal
          sourceAppId={app.id}
          sourceAppName={app.name}
          onClose={() => setShowCopyModal(false)}
        />
      )}
      {showEditModal && (
        <EditAppModal
          app={app}
          onClose={() => setShowEditModal(false)}
        />
      )}
      {showDeleteModal && (
        <DeleteAppModal
          appId={app.id}
          appName={app.name}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
      {showWebhooksModal && (
        <WebhooksModal
          appId={app.id}
          deploymentUrl={app.deployment_url || null}
          prEnabled={!!app.create_pull_requests}
          onClose={() => setShowWebhooksModal(false)}
        />
      )}
      {showSecretsModal && (
        <AppSecretsModal
          entries={secretEntries}
          onClose={() => setShowSecretsModal(false)}
        />
      )}
    </>
  )
}

export default AppActions
